import { zodResolver } from "@hookform/resolvers/zod";
import { ActionIcon, Button, Stack, Title, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { GridColDef } from "@mui/x-data-grid";
import { AtIcon, PencilIcon, PhoneIcon, PlusIcon, TrashIcon, UserIcon } from "@phosphor-icons/react";
import { ArrayPath, Control, FieldArray, FieldValues, SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import z from "zod";

import { REGEX } from "@/lib/zod";

import { Input } from "../_Input";
import { DataGridBase } from "../DataGridBase";

export type ContactSchema = z.infer<typeof contactSchema>;
const contactSchema = z.object({
  id: z.number().default(0),
  fone: z.string().regex(REGEX.PHONE, "Telefone inválido").optional(),
  email: z.email("Email inválido").optional(),
  name: z.string().optional()
  // whatsapp: z.boolean().default(false)
});

type ContactFieldsImplProps<TFieldValues extends FieldValues, TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>> = {
  control: Control<TFieldValues>;
  name: TName;
};

function ContactFieldsImpl<TFieldValues extends FieldValues, TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>>({
  control,
  name
}: ContactFieldsImplProps<TFieldValues, TName>) {
  const {
    handleSubmit,
    control: controlContact,
    reset
  } = useForm({
    mode: "all",
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      fone: ""
      // whatsapp: false
    }
  });

  const { fields, append, remove, update } = useFieldArray<TFieldValues, TName>({
    keyName: "_id" as "id",
    control,
    name
  });

  const onSubmit = (data: ContactSchema) => {
    append(data as unknown as FieldArray<TFieldValues, TName>);
    reset();
  };

  const editContact = (index: number) => {
    const contact = fields[index] as ContactSchema;

    modals.open({
      title: "Atualizar contato",
      children: (
        <EditContactForm
          contact={contact}
          onSave={(updatedContact) => {
            update(index, updatedContact as unknown as FieldArray<TFieldValues, TName>);
            modals.closeAll();
          }}
          onCancel={() => modals.closeAll()}
        />
      )
    });
  };

  const columns: GridColDef<(typeof fields)[number]>[] = [
    { field: "id", headerName: "ID", flex: 1, editable: true, minWidth: 100 },
    { field: "name", headerName: "Nome", flex: 1, editable: true, minWidth: 150 },
    { field: "email", headerName: "Email", flex: 1, editable: true, minWidth: 200 },
    { field: "fone", headerName: "Telefone", flex: 1, editable: true, minWidth: 150 },
    {
      field: "actions",
      type: "actions",
      headerName: "Ações",
      renderCell: ({ row }) => {
        const index = fields.findIndex((field) => field.id === row.id);

        return (
          <ActionIcon.Group>
            <Tooltip label="Editar Contato">
              <ActionIcon color="yellow" variant="light" onClick={() => editContact(index)} radius="sm">
                <PencilIcon />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Remover Contato">
              <ActionIcon color="red" variant="light" onClick={() => remove(index)} radius="sm">
                <TrashIcon />
              </ActionIcon>
            </Tooltip>
          </ActionIcon.Group>
        );
      }
    }
  ];

  return (
    <Stack>
      <div className="flex items-center justify-between">
        <Title order={3}>Contatos</Title>

        <Button className="self-end" color="blue" onClick={handleSubmit(onSubmit)} leftSection={<PlusIcon weight="bold" />}>
          Adicionar Contato
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Input.Text control={controlContact} name="name" label="Nome" placeholder="Exemplo" leftSection={<UserIcon />} />
        <Input.Text control={controlContact} name="email" label="Email" placeholder="exemplo@gmail.com" leftSection={<AtIcon />} />
        <Input.Text control={controlContact} name="fone" label="Telefone" placeholder="(00) 00000-0000" maskProps={{ mask: "(00) 00000-0000" }} leftSection={<PhoneIcon />} />
      </div>

      <DataGridBase
        rows={fields}
        columns={columns}
        hideFooter={fields.length <= 5}
        columnVisibilityModel={{
          id: false
        }}
        initialState={{
          pagination: {
            paginationModel: {
              page: 0,
              pageSize: 5
            }
          }
        }}
        slots={{
          noRowsOverlay: () => <div className="grid h-full place-content-center">Sem contatos</div>
        }}
      />
    </Stack>
  );
}

export const ContactFields = Object.assign(ContactFieldsImpl, {
  $schema: contactSchema
});

//! ================================ Edit Contact Form =================================
type EditContactFormProps = {
  contact: ContactSchema;
  onSave: (data: ContactSchema) => void;
  onCancel: () => void;
};
export function EditContactForm({ contact, onSave, onCancel }: EditContactFormProps) {
  const { id, fone, email, name } = contact;

  const {
    formState: { isDirty },
    handleSubmit,
    control
  } = useForm({
    mode: "all",
    resolver: zodResolver(contactSchema),
    defaultValues: {
      id,
      name,
      fone,
      email
      // whatsapp: false
    }
  });

  const onSubmit: SubmitHandler<ContactSchema> = (data) => {
    onSave(data);
  };

  return (
    <Stack>
      <Input.Text name="name" control={control} label="Nome" placeholder="Digite o nome do contato" withAsterisk />

      <Input.Text name="email" control={control} label="Email" placeholder="Digite o email do contato" withAsterisk />

      <Input.Text name="fone" control={control} label="Telefone" placeholder="Digite o telefone do contato" maskProps={{ mask: "(00) 00000-0000" }} withAsterisk />

      {/* <Input.Text name="whatsapp" control={control} label="WhatsApp" placeholder="Digite o WhatsApp do contato" maskProps={{ mask: "(00) 00000-0000" }} /> */}

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <Button variant="default" onClick={onCancel}>
          Cancelar
        </Button>

        <Button onClick={handleSubmit(onSubmit)} disabled={!isDirty}>
          Salvar
        </Button>
      </div>
    </Stack>
  );
}
